<!-- SOURCE: https://codepen.io/thibautg/pen/dJVNwm -->
<!-- https://blog.risingstack.com/d3-js-tutorial-bar-charts-with-javascript/ -->

<template>
  <div
    :style="{height: height + 'px', width: width + 'px'}"
    class="bar-chart mt-3"
    :class="{disabled: disabled}"
  >
    <h2 class="text-black mb-3">
      {{ "This " + capitalise(type) }}
      <span v-if="isReady">
        vs.
        <span v-for="(item, key, index) in itemsActive">
          {{ key !== type ? `${index > 1 ? '/ ' : ''}` + capitalise(key) : '' }}
        </span>
        Average
      </span>
    </h2>

    <div class="bar-chart__wrapper">
      <svg
        v-if="isReady"
        :width="svgWidth"
        :height="svgHeight"
        @mousemove="mouseover"
      >
        <g
          :transform="`translate(${margin.left},${margin.top})`"
        >
          <g
            v-if="scaleX && items"
            :transform="`translate(0,${paddedHeight})`"
          >
            <axis-x
              :scale-x="scaleX"
              :showTickText="true"
              :width="paddedWidth"
              :type="'barchart'"
              :disabled="disabled"
            />
          </g>

          <g v-if="scaleY && items">
            <axis-y
              :scale-y="scaleY"
              :ceil="ceil"
              :width="paddedWidth"
              :height="paddedHeight"
              :type="`barchart${capitalise(type)}`"
              :disabled="disabled"
            />
          </g>

          <g
            v-if="!disabled"
            class="selector-wrapper"
          >
            <rect
              :x="selector.x"
              :y="0"
              :width="selectorWidth"
              :height="paddedHeight"
              class="selector"
              :class="{'d-none': !selector.visible}"
            />
          </g>

          <g
            v-for="(item,index) in items"
            :key="index"
            :class="item.name"
            :transform="`translate(${groupWidth*index + groupWidth/4},0)`"
          >
            <rect-item
              v-for="(val,cat) in item.val"
              :key="item.name + '-' + cat"
              :item="{ val: val, name: cat, displayName: legendItems[cat].name}"
              :scaleX="scaleXGroup"
              :scaleY="scaleY"
              :height="paddedHeight"
              :color="getColor(cat)"
            />
          </g>
        </g>
      </svg>

      <div
        v-else
        class="loading-wrapper"
        :style="{height: svgHeight + 'px', width: svgWidth + 'px'}"
      >
        <loading-icon
          :loading="!isReady"
          loading-class="text-center vertical-middle"
        />
      </div>

      <div
        v-if="isReady && !disabled"
        :style="{left: popupX + 'px'}"
        class="popup"
        :class="{'d-none': !selector.visible}"
      >
        <div class="popup-title font-weight-bold float-left">
          {{ capitalise(selector.title) }}
        </div>
        <icon
          class="float-right"
          style="width: .85rem;height: .85rem;cursor:pointer;"
          icon="closedark"
          @click.native="closePopup"
        />
        <div :class="`categories ${Object.keys(selector.categories).length === 4 ? 'four-children' : ''}`">
          <div
            v-for="(val,key) in selector.categories"
            :key="key"
            :class="`category ${key}`"
          >
            <span class="cat-text">
              {{ key in legendItems ? legendItems[key].name : key }}
            </span><span class="cat-value font-weight-bold">
              {{ val }}
            </span>
          </div>
        </div>

        <div
          class="popup-tip"
        />
      </div>
    </div>


    <div
      v-if="legendItems && items && !disabled"
      class="legend-wrapper"
    >
      <category-legend
        :items="legendItemsActive"
        :items-active="itemsActive"
        :client-width="clientWidth"
      />
    </div>
  </div>
</template>

<script>
import d3 from '@/analytics/importers/d3-importer'
import {capitalise} from '@/analytics/utils/data-analyser-utils'

import AxisX from './AxisX'
import AxisY from './AxisY'
import RectItem from './RectItem'
import CategoryLegend from './CategoryLegend'

const props = {
  items: {
    type: Array,
  },
  margin: {
    type: Object,
    default: () => ({
      left: 70,
      right: 10,
      top: 10,
      bottom: 45,
    }),
  },
  width: {
    type: Number,
    default: 300,
  },
  clientWidth: {
    type: Number,
    default: null,
  },
  minWidth: {
    type: Number,
    default: null,
  },
  height: {
    type: Number,
    default: 300,
  },
  type: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
}

const defaultSelector = {
  visible: false,
  x: 0,
  width: 0,
  title: 'totals',
  categories: {
    main: 0,
    channel: 0,
    network: 0,
  },
}


export default {
  name: 'BarChart',
  components: {
    AxisX,
    AxisY,
    RectItem,
    CategoryLegend,
  },
  props: props,
  data () {
    return {
      legendItems: {
        [this.type]: {name: 'This ' + this.capitalise(this.type), color: '#0e094d'},
        channel: {name: 'Channel Average', color: '#0364f6'},
        network: {name: 'Network Average', color: '#62ddc7'},
      },
      selector: defaultSelector,
      lastSelectorIndex: null,
    }
  },
  computed: {
    svgWidth () {
      return Math.max(this.minWidth, this.width - 28 * 2)
    },
    svgHeight () {
      const minus = this.disabled ? 30 : 95

      return this.width < this.svgWidth
        ? this.height - 24 * 2 - minus - 60
        : this.height - 24 * 2 - minus
    },
    paddedWidth () {
      return this.svgWidth - this.margin.left - this.margin.right
    },
    paddedHeight () {
      return this.svgHeight - this.margin.top - this.margin.bottom
    },
    groupWidth () {
      return this.paddedWidth / this.items.length
    },
    ceil () {
      const maxY = Math.max.apply(Math, Object.entries(this.items).map((it) => Math.max(...Object.entries(it[1].val).map((it2) => it2[1]))), 10)

      return Math.max(5, Math.ceil(maxY + maxY * 0.2))
    },
    groupLabels () {
      return this.items.map((x) => x.name)
    },
    scaleX () {
      return d3.scaleBand()
          .range([0, this.paddedWidth])
          .domain(this.items.map((x) => x.name))
    },
    scaleXGroup () {
      return d3.scaleBand()
          .range([0, this.groupWidth / 2]).padding(0.15)
          .domain(Object.keys(this.items[0].val))
    },
    scaleY () {
      return d3.scaleLinear()
          .range([this.paddedHeight, 0])
          .domain([0, this.ceil])
    },
    itemsActive () {
      return this.items[0].val
    },
    legendItemsActive () {
      const items = {...this.legendItems}
      for (const key in items) {
        if (items[key]) {
          items[key].color = this.getColor(key)
        }
      }

      return items
    },
    isReady () {
      return this.items != null
    },
    selectorWidth () {
      return this.groupWidth
    },
    popupX () {
      return this.selector.x + this.groupWidth / 2 - 30
    },
  },
  methods: {
    getColor (key) {
      return Object.keys(this.legendItems).includes(key) ? this.legendItems[key].color : 'grey'
    },
    capitalise (str) {
      return capitalise(str)
    },
    mouseover (params) {
      if (this.items.length > 0) {
        const x = params.offsetX - this.margin.left
        const ind = Math.min(Math.max(Math.floor(x/this.groupWidth), 0), this.items.length - 1)
        if (this.lastSelectorIndex !== ind) {
          const sel = {
            visible: true,
            x: ind === 0 ? 0 : ind * this.groupWidth,
            title: this.groupLabels[ind] !== undefined ? this.groupLabels[ind] : 'Totals',
            index: ind,
            categories: {},
          }

          for (const prop in this.items[ind].val) {
            if (this.items[ind].val[prop] > -1) {
              sel.categories[prop] = this.items[ind].val[prop]
            }
          }

          this.selector = sel
          this.lastSelectorIndex = ind
        }
      }
    },
    closePopup () {
      this.$set(this.selector, 'visible', false)
    },
  },
}
</script>

<style lang="scss" scoped>
  .bar-chart {
    padding: 24px;
    border: 1px solid #dfe2ee;
    border-radius: 2px;
    display: inline-block;
    position: relative;
    overflow: hidden;
    &__wrapper {
      overflow: hidden;
      overflow-x: auto;
      position: relative;
      svg {
        shape-rendering: crispEdges
      }
      .popup {
        width: 190px;
      }
    }
    &.disabled {
      color: #98a0bc !important;
      svg {
        cursor: no-drop;
      }
      rect {
        display: none;
      }
      div, span, h2, text {
        color: #98a0bc !important;
      }
      .axis-wrapper {
        color: #98a0bc !important;
      }
    }
    .legend-wrapper {
      margin: 8px 0 0 24px;
    }

  }

</style>
